import { useField } from "formik";
import { SelectControl } from "formik-chakra-ui";
import React from "react";
import { useGetEligibleFormTemplatesForApplicant } from "src/generated/hooks/form-service/queries";
import type { EligibleFormTemplateSummary } from "src/generated/hooks/form-service/requests";

interface EligibleFormTemplateSelectInputProps {
  name: string;
  enrollmentPeriodId: string;
  applicantId: string;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  onSelectedFormTemplate?: (
    selected: EligibleFormTemplateSummary | null | undefined
  ) => void;
}

export function EligibleFormTemplateSelectInput({
  name,
  enrollmentPeriodId,
  applicantId,
  label = "Form",
  placeholder = "Select a form",
  isRequired = false,
  isDisabled = false,
  onSelectedFormTemplate,
}: EligibleFormTemplateSelectInputProps) {
  const [hasUserSelected, setHasUserSelected] = React.useState(false);
  const [field, { initialValue }, helper] = useField(name);

  const { data: templatesResponse } = useGetEligibleFormTemplatesForApplicant(
    {
      query: {
        enrollmentPeriodId,
        applicantId,
      },
    },
    undefined,
    { enabled: !!enrollmentPeriodId && !!applicantId }
  );
  const templates = React.useMemo(
    () => templatesResponse?.data ?? [],
    [templatesResponse?.data]
  );

  React.useEffect(() => {
    if (templates.length > 0 && field.value) {
      const found = templates.some((t) => t.id === field.value);
      if (!found) {
        helper.setValue("");
        onSelectedFormTemplate?.(null);
      }
    }
  }, [templates, field.value, helper, onSelectedFormTemplate]);

  React.useEffect(() => {
    if (!hasUserSelected && templates.length > 0 && initialValue) {
      const preSelected = templates.find((t) => t.id === initialValue);
      if (preSelected) {
        onSelectedFormTemplate?.(preSelected);
      }
    }
  }, [templates, hasUserSelected, initialValue, onSelectedFormTemplate]);

  const handleChange = (value: string) => {
    setHasUserSelected(true);
    const selected = templates.find((t) => t.id === value) ?? null;
    helper.setTouched(true, false);
    helper.setValue(selected?.id ?? "", true);
    onSelectedFormTemplate?.(selected);
  };

  return (
    <SelectControl
      name={name}
      label={label}
      isRequired={isRequired}
      selectProps={{
        placeholder,
        onChange: (event) => {
          handleChange(event.target.value);
        },
        isDisabled,
      }}
    >
      {templates.map((template) => (
        <option key={template.id} value={template.id}>
          {template.name}
        </option>
      ))}
    </SelectControl>
  );
}
